/**
 * The name to put on screen for a skill — one function per question asked.
 *
 * Three different shapes carry a skill's name and each answers a different
 * question, so each has its own function rather than one overload pretending
 * they are the same: a HELD skill on a practitioner's fold, a CATALOGUE skill
 * from the tenant's knowledge, and a TEAM-SKILL row from a team's demand read.
 * They sit beside `localisedTitle`, which is the locale walk all three of the
 * catalogue-shaped ones share.
 *
 * None of these is a fallback chain standing in for missing data. Each step is
 * a real home for the name, and the last step is an identifier the server
 * computed — the honest thing to show, and one a reader can still act on,
 * because it is how the skill is addressed in every path.
 */

import type { HeldSkill, Skill } from '@/api/endpoints/skills';
import type { TeamSkillRow } from '@/api/endpoints/teamSkills';
import { localisedTitle } from '@/lib/localisedTitle';
import { localId, referenceLabel } from '@/lib/reference';

/**
 * The name of one folded skill.
 *
 * `label` is the practitioner's own words (null when every record behind the
 * skill named a catalogue skill), `skill.display` is the label the pointer
 * carries, `catalogueTitle` reads the tenant catalogue for a skill the fold
 * names by id, and `key` is how the skill is addressed in a path.
 *
 * THE CATALOGUE STEP IS NOT OPTIONAL DECORATION, and leaving it out is what
 * this fixed. A fold names a catalogue skill by id and nothing more:
 * `skillRef` in the gateway builds `Skill/<id>` with an EMPTY display, on
 * purpose, because a title is knowledge that carries every supported locale and
 * the server does not get to pick one for the reader. So a target set on
 * `advanced-life-support` rendered `advanced-life-support` on a nurse's board,
 * where `Erweiterte Reanimation` belongs. The id is a correct last resort and a
 * poor first one.
 *
 * `catalogueTitle` is therefore a lookup the CALLER supplies, having already
 * chosen the locale — `catalogueSkillTitle` below is what fills it. A caller
 * that has not loaded the catalogue passes nothing and gets the key, which is
 * the same honest answer as before rather than a blank.
 */
export function heldSkillTitle(
  skill: Pick<HeldSkill, 'key' | 'label' | 'skill'>,
  catalogueTitle?: (skillId: string) => string | undefined,
): string {
  if (skill.label) return skill.label;
  if (skill.skill?.display) return skill.skill.display;
  const id = localId(skill.skill, 'Skill');
  if (id && catalogueTitle) {
    const title = catalogueTitle(id);
    if (title) return title;
  }
  return skill.key;
}

/**
 * A lookup from catalogue skill id to the name to show this reader, for
 * `heldSkillTitle` above.
 *
 * Built once per catalogue rather than searched per row: a board renders every
 * target it holds and a linear scan for each one is a loop over knowledge that
 * does not change between rows.
 */
export function catalogueTitleLookup(
  skills: Skill[],
  language: string,
): (skillId: string) => string | undefined {
  const byID = new Map(skills.map((s) => [s.id, catalogueSkillTitle(s, language)]));
  return (skillId) => byID.get(skillId);
}

/**
 * The name of one CATALOGUE skill.
 *
 * The catalogue carries every supported locale, so the reader picks one. The id
 * is the last resort and an honest one: it is how the skill is addressed in
 * every path, so a lead who sees it can still act on it.
 */
export function catalogueSkillTitle(skill: Skill, language: string): string {
  return localisedTitle(skill.title, language) ?? skill.id;
}

/**
 * The name of one team-skill row.
 *
 * The locale walk is the shared one — the tenant skill catalogue answers the
 * same question about the same shape. What is local to a team-skill row is what
 * to show when no locale carries a title: the pointer's own answer, read with
 * the house Reference helpers rather than by slicing the string here. `localId`
 * gives the id of a skill we hold, which is how that skill is addressed in
 * every path; `referenceLabel` gives what a pointer to somewhere else carries —
 * its display label, its business key, or the URL itself.
 */
export function teamSkillTitle(row: TeamSkillRow, language: string): string {
  const title = localisedTitle(row.title, language);
  if (title) {
    return title;
  }
  return localId(row.skill, 'Skill') ?? referenceLabel(row.skill);
}
